import classNames from 'classnames'

import { useMouseHover } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { stopPropagation } from '../../../utils/events'
import { Checkbox, CHECKBOX_SIZE, CHECKBOX_TYPE } from '../../Checkbox'
import { ListItemAvatar } from '../ListItemAvatar'
import { ListItemContent } from '../ListItemContent'
import { ListItemContentContainer } from '../ListItemContentContainer'
import { ListItemIcons } from '../ListItemIcons'

import { QA_NAME_LIST_ITEM_CONTEXT } from './ListItem.constants'
import { isNeedToShowListItemIcons } from './ListItem.utils'

import * as styles from './ListItem.module.css'

function ListItem({
  Avatar = ListItemAvatar,
  Content = ListItemContent,
  Icons = ListItemIcons,
  ListIcon,
  avatar,
  avatarProps,
  blocked,
  className,
  contentClassName,
  contentContainerProps,
  description,
  disabled,
  hoverClassName = styles.hover,
  iconsProps,
  iconTooltip,
  isActive,
  isArchived,
  isFocused,
  isMulti,
  isNeedToShowIcons = isNeedToShowListItemIcons,
  qaName,
  userStatus,
  value,
  onClick,
  onDoubleClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
  ...contentProps
}) {
  const [ref, isHovered] = useMouseHover({
    onMouseEnter,
    onMouseLeave,
  })

  function handleClick(event) {
    stopPropagation(event)

    if (!disabled && !blocked) {
      onClick?.(value, event)
    }
  }

  function handleDoubleClick(event) {
    stopPropagation(event)

    if (!disabled && !blocked) {
      onDoubleClick?.(value)
    }
  }

  function handleMouseDown(event) {
    if (!disabled && !blocked) {
      onMouseDown?.(value, event)
    }
  }

  const listItemClassName = classNames(
    styles.ListItem,
    {
      [hoverClassName]: !!hoverClassName && isHovered && !blocked,
      [styles.active]: isActive || isFocused,
      [styles.blocked]: blocked,
      [styles.disabled]: disabled,
      [styles.withDescription]: !!description,
    },
    className
  )

  const qaProps = getQaSelector({
    context: QA_NAME_LIST_ITEM_CONTEXT,
    def: QA_NAME_LIST_ITEM_CONTEXT,
    value: qaName,
  })

  const showIcons = isNeedToShowIcons({
    Icons,
    isActive,
    isArchived,
    isHovered,
    isMulti,
    userStatus,
  })

  return (
    <li
      {...qaProps}
      className={listItemClassName}
      ref={ref}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
    >
      <ListItemContentContainer {...contentContainerProps}>
        {isMulti ? (
          <Checkbox
            checked={isActive}
            className={styles.checkbox}
            size={CHECKBOX_SIZE.small}
            type={CHECKBOX_TYPE.table}
          />
        ) : null}
        {Avatar && (avatar || ListIcon) ? (
          <Avatar
            {...avatarProps}
            Icon={ListIcon}
            avatar={avatar}
            iconTooltip={iconTooltip}
          />
        ) : null}
        <Content
          {...contentProps}
          blocked={blocked}
          className={contentClassName}
          description={description}
          disabled={disabled}
          isActive={isActive}
          isFocused={isFocused}
          isHovered={isHovered}
          value={value}
        />
        {showIcons ? (
          <Icons
            {...iconsProps}
            isArchived={isArchived}
            isHovered={isHovered}
            isSelected={isActive && !isMulti}
            userStatus={userStatus}
          />
        ) : null}
      </ListItemContentContainer>
    </li>
  )
}

export default ListItem
